(() => {
  'use strict';

  const widgets = Array.from(document.querySelectorAll('section.cap-sr1-advanced[data-cap-sr1-advanced]'));
  if (!widgets.length) return;

  const SUPABASE_URL = 'https://vumyshrwjivysgplsgrf.supabase.co';
  const SUPABASE_KEY = 'sb_publishable_wDS4N9-_DV-SAFTaSsgTPg_kRDTuYYk';
  const RESOLVER_RPC = 'rpc_storefront_rep_advanced_identifier_search_v1';
  const FIND_PARTS_URL = '/pages/find-parts';
  const candidateContexts = new WeakMap();
  const widgetState = new WeakMap();
  const clean = (value) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  const INPUT_PLACEHOLDER = 'Stock number, VIN, or yard location';

  const delay = (milliseconds, signal) => new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener('abort', () => {
      window.clearTimeout(timeout);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });

  function setStatus(widget, message) {
    const status = widget.querySelector('[data-cap-sr1-status]');
    if (status) status.textContent = message || '';
  }

  function clearResults(widget) {
    const results = widget.querySelector('[data-cap-sr1-results]');
    if (!results) return;
    results.replaceChildren();
    results.hidden = true;
  }

  function cancelLookup(widget) {
    const current = widgetState.get(widget);
    current?.controller.abort();
    widgetState.delete(widget);
  }

  function rootFor(widget) {
    return widget.closest('#cap-home-fitment-search, #cap-find-parts-app');
  }

  function isVehicleCandidate(candidate) {
    return clean(candidate?.result_kind).toLowerCase() === 'vehicle';
  }

  function donorUrl(candidate) {
    const year = clean(candidate?.year);
    const make = clean(candidate?.make);
    const model = clean(candidate?.model);
    const collectionGid = clean(candidate?.shopify_collection_gid);
    const productGid = clean(candidate?.shopify_product_gid);
    if (!year || !make || !model || !collectionGid) return '';

    const url = new URL(FIND_PARTS_URL, window.location.origin);
    url.searchParams.set('mode', 'parts');
    url.searchParams.set('year', year);
    url.searchParams.set('make', make);
    url.searchParams.set('model', model);
    url.searchParams.set('vehicle_collection', collectionGid);
    url.searchParams.set('sr1_donor', '1');
    if (!isVehicleCandidate(candidate) && productGid) {
      url.searchParams.set('product_gid', productGid);
    }
    return `${url.pathname}?${url.searchParams.toString()}`;
  }

  function productType(candidate) {
    return clean(candidate?.part_subcategory || candidate?.part_name || candidate?.product_title || 'Inventory Product');
  }

  function candidateLabel(candidate) {
    const vehicle = [candidate?.year, candidate?.make, candidate?.model].map(clean).filter(Boolean).join(' ') || 'Matching inventory';
    return isVehicleCandidate(candidate)
      ? `${vehicle} — ${clean(candidate?.product_title) || 'Vehicle'}`
      : `${vehicle} — ${productType(candidate)}`;
  }

  function candidateMeta(candidate) {
    const yard = clean(candidate?.yard) || 'Callahan';
    if (isVehicleCandidate(candidate)) {
      const location = clean(candidate?.yard_location) || 'No recorded location';
      return `Yard Location match · ${location} · ${yard}`;
    }
    return `${clean(candidate?.matched_by).toLowerCase() === 'vin' ? 'Full VIN' : 'Stock Number'} match · ${yard}`;
  }

  function openCandidate(widget, candidate) {
    const url = donorUrl(candidate);
    if (!url) {
      setStatus(widget, 'This match is missing vehicle identity. Try another identifier.');
      return;
    }
    setStatus(widget, isVehicleCandidate(candidate) ? 'Opening the vehicle parts list...' : `Opening ${productType(candidate)}...`);
    window.location.assign(url);
  }

  function renderCandidates(widget, rows) {
    const candidates = (Array.isArray(rows) ? rows : [])
      .filter((row) => donorUrl(row))
      .sort((left, right) => Number(isVehicleCandidate(right)) - Number(isVehicleCandidate(left)));

    if (!candidates.length) {
      clearResults(widget);
      setStatus(widget, 'No matching inventory was found.');
      return;
    }

    if (candidates.length === 1 && !isVehicleCandidate(candidates[0])) {
      openCandidate(widget, candidates[0]);
      return;
    }

    const results = widget.querySelector('[data-cap-sr1-results]');
    if (!results) return;
    results.replaceChildren();
    candidates.forEach((candidate) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cap-sr1-advanced__result';
      button.setAttribute('role', 'option');

      const title = document.createElement('strong');
      title.textContent = candidateLabel(candidate);
      const match = document.createElement('small');
      match.textContent = candidateMeta(candidate);
      button.append(title, match);
      candidateContexts.set(button, candidate);
      results.appendChild(button);
    });
    results.hidden = false;
    setStatus(widget, `${candidates.length} ${candidates.length === 1 ? 'match' : 'matches'} found. Choose one to open its parts list.`);
  }

  async function requestCandidates(query, signal) {
    const retryableStatuses = new Set([408, 425, 429, 500, 502, 503, 504]);
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${RESOLVER_RPC}`, {
          method: 'POST',
          headers: {
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${SUPABASE_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ p_query: query, p_scope: 'any' }),
          signal
        });
        if (response.ok) {
          const rows = await response.json();
          if (!Array.isArray(rows)) throw new TypeError('Advanced search returned an invalid response.');
          return rows;
        }
        const error = new Error(`Advanced search failed: ${response.status}`);
        error.retryable = retryableStatuses.has(response.status);
        throw error;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        lastError = error;
        if (attempt === 1 || error?.retryable === false) break;
        await delay(250, signal);
      }
    }

    throw lastError || new Error('Advanced search failed.');
  }

  async function searchIdentifier(widget) {
    const input = widget.querySelector('[data-cap-sr1-input]');
    const submit = widget.querySelector('[data-cap-sr1-submit]');
    const query = clean(input?.value);
    if (query.length < 2) {
      setStatus(widget, 'Enter an identifier.');
      input?.focus();
      return;
    }

    cancelLookup(widget);
    clearResults(widget);
    const controller = new AbortController();
    widgetState.set(widget, { controller });
    if (submit) submit.disabled = true;
    if (input) input.setAttribute('aria-busy', 'true');
    setStatus(widget, 'Searching...');

    try {
      renderCandidates(widget, await requestCandidates(query, controller.signal));
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setStatus(widget, 'Advanced Search is temporarily unavailable. Please retry.');
      }
    } finally {
      if (widgetState.get(widget)?.controller === controller) {
        widgetState.delete(widget);
        if (submit) submit.disabled = false;
        if (input) input.removeAttribute('aria-busy');
      }
    }
  }

  function bindWidget(widget) {
    const form = widget.querySelector('[data-cap-sr1-form]');
    const input = widget.querySelector('[data-cap-sr1-input]');
    const results = widget.querySelector('[data-cap-sr1-results]');
    const root = rootFor(widget);

    if (input) input.placeholder = INPUT_PLACEHOLDER;

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      void searchIdentifier(widget);
    });

    input?.addEventListener('input', () => {
      cancelLookup(widget);
      clearResults(widget);
      setStatus(widget, '');
    });

    results?.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('.cap-sr1-advanced__result');
      if (!button || !results.contains(button)) return;
      openCandidate(widget, candidateContexts.get(button));
    });

    root?.querySelector('.cap-home-fitment__form, .cap-find-parts__filters')?.addEventListener('submit', () => cancelLookup(widget));
    root?.querySelector('.cap-home-fitment__reset, .cap-find-parts__clear')?.addEventListener('click', () => {
      cancelLookup(widget);
      clearResults(widget);
      if (input) input.value = '';
      setStatus(widget, '');
    });
  }

  function applyVerifiedVisibility(detail) {
    const isSalesRep = Boolean(detail && detail.authorized === true);
    widgets.forEach((widget) => {
      widget.hidden = !isSalesRep;
      if (!isSalesRep) {
        cancelLookup(widget);
        clearResults(widget);
        setStatus(widget, '');
      }
    });
  }

  widgets.forEach(bindWidget);

  try {
    const gate = window.CAP_SR1;
    if (!gate || typeof gate.getState !== 'function') {
      applyVerifiedVisibility(null);
      return;
    }

    document.addEventListener('cap:sr1-state', (event) => {
      applyVerifiedVisibility(event?.detail);
    });
    applyVerifiedVisibility(gate.getState());
    if (gate.ready && typeof gate.ready.then === 'function') {
      gate.ready.then(applyVerifiedVisibility).catch(() => applyVerifiedVisibility(gate.getState()));
    }
  } catch (error) {
    applyVerifiedVisibility(null);
  }
})();

